import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { FiCheck, FiChevronLeft, FiChevronRight, FiSettings, FiX } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { apiClient } from '../../api/client'
import { API_ENDPOINTS } from '../../api/endpoints'
import { useLanguageStore } from '../../store/languageStore'
import { useUserDefaultsStore } from '../../store/userDefaultsStore'
import { useAuthStore } from '../../store/authStore'
import BranchDropdown from './BranchDropdown'
import StoreDropdown from './StoreDropdown'
import AccountDropdown from './AccountDropdown'
import CustomerDropdown from './CustomerDropdown'

type WizardForm = {
  branchId?: string
  storeId?: string
  cashAccountId?: string
  contactId?: string
}

const STEPS = [
  { key: 'branch', ar: 'الفرع الافتراضي', en: 'Default branch' },
  { key: 'store', ar: 'المخزن الافتراضي', en: 'Default store' },
  { key: 'cash', ar: 'الحساب الخزيني', en: 'Default cash account' },
  { key: 'customer', ar: 'العميل الافتراضي (اختياري)', en: 'Default customer (optional)' },
] as const

interface FirstRunWizardProps {
  /** When true, user must finish before using the app */
  blocking?: boolean
  onCompleted?: () => void
}

export default function FirstRunWizard({ blocking = true, onCompleted }: FirstRunWizardProps) {
  const { language } = useLanguageStore()
  const isRTL = language === 'ar'
  const queryClient = useQueryClient()
  const mergeUser = useAuthStore((s) => s.mergeUser)
  const { defaults, setFromApi, setForceWizardOpen } = useUserDefaultsStore()
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<WizardForm>({
    branchId: defaults.branchId,
    storeId: defaults.storeId,
    cashAccountId: defaults.cashAccountId,
    contactId: defaults.contactId,
  })

  const dismiss = () => {
    if (blocking) return
    setForceWizardOpen(false)
    onCompleted?.()
  }

  const ChevronNext = isRTL ? FiChevronLeft : FiChevronRight
  const ChevronBack = isRTL ? FiChevronRight : FiChevronLeft

  const canNext = () => {
    if (step === 0) return !!form.branchId
    if (step === 1) return !!form.storeId
    if (step === 2) return !!form.cashAccountId
    return true
  }

  const finish = async () => {
    if (!form.branchId || !form.storeId || !form.cashAccountId) {
      toast.error(isRTL ? 'أكمل الحقول المطلوبة' : 'Please complete required fields')
      return
    }
    setSaving(true)
    try {
      const { data } = await apiClient.put(API_ENDPOINTS.auth.personalDefaults, {
        branchId: form.branchId,
        storeId: form.storeId,
        cashAccountId: form.cashAccountId,
        contactId: form.contactId || null,
      })
      const personalDefaults = data?.personalDefaults ?? {
        setupCompleted: true,
        branchId: form.branchId,
        storeId: form.storeId,
        cashAccountId: form.cashAccountId,
        contactId: form.contactId,
      }
      setFromApi(personalDefaults)
      setForceWizardOpen(false)
      mergeUser({
        branchCode: form.branchId,
        branchId: form.branchId,
      })
      void queryClient.invalidateQueries({ queryKey: ['invoiceSettings'] })
      toast.success(isRTL ? 'تم حفظ إعداداتك الشخصية' : 'Personal defaults saved')
      onCompleted?.()
    } catch (error: any) {
      toast.error(
        error?.response?.data?.messageAr ||
          error?.response?.data?.message ||
          (isRTL ? 'فشل حفظ الإعدادات' : 'Failed to save settings')
      )
    } finally {
      setSaving(false)
    }
  }

  const goNext = () => {
    if (!canNext()) {
      toast.error(isRTL ? 'هذا الحقل مطلوب' : 'This field is required')
      return
    }
    if (step < STEPS.length - 1) setStep((s) => s + 1)
    else void finish()
  }

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={`absolute inset-0 bg-black/60 ${blocking ? '' : 'bg-black/40'}`} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white p-5">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <FiSettings className="text-xl" />
              <h2 className="text-lg font-bold">
                {isRTL ? 'معالج الإعدادات الأولية' : 'First-run setup wizard'}
              </h2>
            </div>
            {!blocking && (
              <button
                type="button"
                onClick={dismiss}
                className="p-1.5 rounded-lg hover:bg-white/20"
                aria-label={isRTL ? 'إغلاق' : 'Close'}
              >
                <FiX />
              </button>
            )}
          </div>
          <p className="text-sm text-white/90">
            {isRTL
              ? 'حدد قيمك الافتراضية لاستخدامها تلقائياً في الفواتير والسندات والقيود.'
              : 'Set your personal defaults — applied automatically on new documents.'}
          </p>
        </div>

        <div className="px-5 pt-4">
          <div className="flex gap-1 mb-4">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-primary-600' : 'bg-gray-200'}`}
              />
            ))}
          </div>
          <p className="text-sm font-medium text-gray-800 mb-3">
            {isRTL ? STEPS[step].ar : STEPS[step].en}
          </p>

          <div className="min-h-[120px]">
            {step === 0 && (
              <BranchDropdown
                value={form.branchId}
                onChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    branchId: v != null ? String(v) : undefined,
                    storeId: undefined,
                  }))
                }
                autoSelectSingle={false}
              />
            )}
            {step === 1 && (
              <StoreDropdown
                value={form.storeId}
                branchId={form.branchId}
                onChange={(v) => setForm((f) => ({ ...f, storeId: v != null ? String(v) : undefined }))}
                autoSelectSingle={false}
              />
            )}
            {step === 2 && (
              <AccountDropdown
                value={form.cashAccountId}
                onChange={(v) =>
                  setForm((f) => ({ ...f, cashAccountId: v != null ? String(v) : undefined }))
                }
                accountMode="leaf"
              />
            )}
            {step === 3 && (
              <div className="space-y-2">
                <CustomerDropdown
                  value={form.contactId}
                  onChange={(v) =>
                    setForm((f) => ({ ...f, contactId: v != null ? String(v) : undefined }))
                  }
                />
                <p className="text-xs text-gray-500">
                  {isRTL ? 'يمكنك تخطي هذا الحقل إن لم يكن لديك عميل افتراضي.' : 'You can skip this if you have no default customer.'}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="p-5 flex items-center justify-between gap-3 border-t mt-4">
          <button
            type="button"
            disabled={step === 0 || saving}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="flex items-center gap-1 px-4 py-2 rounded-lg border text-gray-700 disabled:opacity-40"
          >
            <ChevronBack />
            {isRTL ? 'السابق' : 'Back'}
          </button>
          <button
            type="button"
            disabled={saving || !canNext()}
            onClick={goNext}
            className="flex items-center gap-1 px-5 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {step === STEPS.length - 1 ? (
              <>
                <FiCheck />
                {saving ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : isRTL ? 'إنهاء' : 'Finish'}
              </>
            ) : (
              <>
                {isRTL ? 'التالي' : 'Next'}
                <ChevronNext />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
